/**
 * Math utilities for common calculations used throughout the application
 */

/**
 * Standard coordinate space for normalized coordinates (0-1000)
 * This is used for storing coordinates in a resolution-independent way
 */
export const COORDINATE_SPACE = 1000;

/**
 * Convert a pixel coordinate to normalized coordinate space (0-1000)
 */
export function toNormalizedCoordinate(pixelValue: number, containerSize: number): number {
  return Math.round((pixelValue / containerSize) * COORDINATE_SPACE);
}

/**
 * Convert a normalized coordinate (0-1000) to percentage for CSS
 */
export function normalizedToPercentage(normalizedValue: number): number {
  return (normalizedValue / COORDINATE_SPACE) * 100;
}

/**
 * Convert a pixel coordinate to percentage
 */
export function pixelToPercentage(pixelValue: number, containerSize: number): number {
  return (pixelValue / containerSize) * 100;
}

/**
 * Calculate the Euclidean distance between two points
 */
export function calculateDistance(x1: number, y1: number, x2: number, y2: number): number {
  return Math.sqrt(Math.pow(x2 - x1, 2) + Math.pow(y2 - y1, 2));
}

/**
 * Calculate the center point between two values
 */
export function calculateCenter(value1: number, value2: number): number {
  return (value1 + value2) / 2;
}

/**
 * Calculate the center point of a rectangle
 */
export function calculateRectCenter(rect: { left: number; top: number; width: number; height: number }): {
  x: number;
  y: number;
} {
  return {
    x: rect.left + rect.width / 2,
    y: rect.top + rect.height / 2,
  };
}

/**
 * Calculate width from bounding box coordinates
 */
export function calculateBoundingBoxWidth(topLeftX: number, bottomRightX: number): number {
  return bottomRightX - topLeftX;
}

/**
 * Calculate height from bounding box coordinates
 */
export function calculateBoundingBoxHeight(topLeftY: number, bottomRightY: number): number {
  return bottomRightY - topLeftY;
}

/**
 * Calculate both width and height from bounding box coordinates
 */
export function calculateBoundingBoxDimensions(
  topLeftX: number,
  topLeftY: number,
  bottomRightX: number,
  bottomRightY: number,
): { width: number; height: number } {
  return {
    width: calculateBoundingBoxWidth(topLeftX, bottomRightX),
    height: calculateBoundingBoxHeight(topLeftY, bottomRightY),
  };
}

/**
 * Clamp a value between min and max bounds
 */
export function clamp(value: number, min: number, max: number): number {
  return Math.max(min, Math.min(value, max));
}

/**
 * Round a number to a specified number of decimal places
 */
export function roundToDecimals(value: number, decimals: number): number {
  const factor = Math.pow(10, decimals);
  return Math.round(value * factor) / factor;
}

/**
 * Calculate the average of two values
 */
export function average(value1: number, value2: number): number {
  return (value1 + value2) / 2;
}

/**
 * Convert radians to degrees
 */
export function radiansToDegrees(radians: number): number {
  return radians * (180 / Math.PI);
}

/**
 * Convert degrees to radians
 */
export function degreesToRadians(degrees: number): number {
  return degrees * (Math.PI / 180);
}

/**
 * Format a number as a percentage string
 */
export function formatPercentage(value: number, decimals: number = 0): string {
  return `${(value * 100).toFixed(decimals)}%`;
}

/**
 * Format a decimal number with fixed decimal places
 */
export function formatDecimal(value: number, decimals: number = 2): string {
  return value.toFixed(decimals);
}
